import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Stack,
    Collapse,
    IconButton,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Add, ExpandMore, ExpandLess } from '@mui/icons-material';
import apiClient from '../../api';
import { useAuth } from '../../context/AuthContext';
import { isOfficePersonnel } from '../../utils/personnelType';

const LABEL_END_DISPATCH = 'Date to Be In';
const LABEL_BEGIN_DISPATCH = 'Date to Leave Back Out';

/** Start of local calendar day for `d` (new Date). */
function startOfLocalDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function derivePtoDates(leavingYardAt, backToYardAt) {
    return {
        startDate: startOfLocalDay(leavingYardAt),
        endDate: startOfLocalDay(backToYardAt),
    };
}

function defaultDispatchDates() {
    const today = startOfLocalDay(new Date());
    return { endDispatchDate: today, beginDispatchDate: today };
}

function buildDispatchAt(dateOnly, timeAt) {
    if (!dateOnly || !timeAt) return null;
    const d = startOfLocalDay(dateOnly);
    const t = new Date(timeAt);
    if (Number.isNaN(t.getTime())) return null;
    d.setHours(t.getHours(), t.getMinutes(), 0, 0);
    return d;
}

function calendarSpanDays(start, end) {
    if (!start || !end) return 0;
    const a = startOfLocalDay(start);
    const b = startOfLocalDay(end);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;
    return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

function isSameLocalDay(a, b) {
    return calendarSpanDays(a, b) === 1;
}

function toHhMm(timeAt) {
    if (!timeAt) return null;
    const t = new Date(timeAt);
    if (Number.isNaN(t.getTime())) return null;
    return `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
}

function createInitialFormData() {
    const { endDispatchDate, beginDispatchDate } = defaultDispatchDates();
    return {
        requestType: 'vacation_paid',
        paidVacationDays: 1,
        reason: '',
        endDispatchDate,
        endTimeAt: null,
        beginDispatchDate,
        beginTimeAt: null,
        absenceStartDate: endDispatchDate,
        absenceEndDate: endDispatchDate,
        dayMode: 'all',
        leaveTimeAt: null,
        backTimeAt: null,
    };
}

function validateDispatchTime(label, timeAt) {
    if (!timeAt) {
        return `${label}: time is required.`;
    }
    return null;
}

function DispatchScheduleBlock({
    dateLabel,
    dateValue,
    onDateChange,
    timeAt,
    onTimeAtChange,
    minDate,
}) {
    return (
        <Stack spacing={1.5}>
            <DatePicker
                label={dateLabel}
                value={dateValue}
                onChange={onDateChange}
                minDate={minDate}
                slotProps={{
                    textField: {
                        fullWidth: true,
                        required: true,
                    },
                }}
            />
            <TimePicker
                label="Time"
                value={timeAt}
                onChange={onTimeAtChange}
                ampm
                minutesStep={15}
                slotProps={{
                    textField: {
                        fullWidth: true,
                        required: true,
                    },
                }}
            />
        </Stack>
    );
}

const TimeOffRequestForm = ({ onSubmitSuccess, defaultOpen = false, compact = false }) => {
    const { user } = useAuth();
    const isOffice = isOfficePersonnel(user);
    const [formOpen, setFormOpen] = useState(Boolean(defaultOpen));
    useEffect(() => {
        if (compact) {
            setFormOpen(true);
        }
    }, [compact]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [formData, setFormData] = useState(createInitialFormData);

    const requestTypes = [
        { value: 'sick_doctor', label: 'Sick/Doctor' },
        { value: 'vacation_paid', label: 'Vacation-Paid' },
        { value: 'bereavement', label: 'Bereavement' },
        { value: 'unpaid', label: 'Time Off Without Pay' },
        { value: 'military', label: 'Military' },
        { value: 'jury_duty', label: 'Jury Duty' },
        { value: 'maternity_paternity', label: 'Maternity/Paternity' },
        { value: 'other', label: 'Other' }
    ];

    const patchForm = (patch) => {
        setFormData((prev) => ({ ...prev, ...patch }));
        if (error) setError('');
    };

    const buildDatetimesFromForm = () => {
        const leavingYardAt = buildDispatchAt(formData.endDispatchDate, formData.endTimeAt);
        const backToYardAt = buildDispatchAt(formData.beginDispatchDate, formData.beginTimeAt);
        return { leavingYardAt, backToYardAt };
    };

    const validateForm = () => {
        if (isOffice) {
            if (!formData.absenceStartDate || !formData.absenceEndDate) {
                setError('Please set From and To dates for the absence.');
                return false;
            }
            const start = startOfLocalDay(formData.absenceStartDate);
            const end = startOfLocalDay(formData.absenceEndDate);
            if (end < start) {
                setError('To date cannot be before From date.');
                return false;
            }
            if (formData.dayMode === 'partial') {
                if (!isSameLocalDay(formData.absenceStartDate, formData.absenceEndDate)) {
                    setError('Partial day can only be used when From and To are the same date.');
                    return false;
                }
                if (!formData.leaveTimeAt) {
                    setError('Leave time is required for a partial day.');
                    return false;
                }
                if (!formData.backTimeAt) {
                    setError('Back time is required for a partial day.');
                    return false;
                }
                const leaveAt = buildDispatchAt(formData.absenceStartDate, formData.leaveTimeAt);
                const backAt = buildDispatchAt(formData.absenceStartDate, formData.backTimeAt);
                if (!leaveAt || !backAt || backAt.getTime() <= leaveAt.getTime()) {
                    setError('Back time must be after leave time.');
                    return false;
                }
            }
        } else {
            if (!formData.endDispatchDate || !formData.beginDispatchDate) {
                setError(`Please set dates for ${LABEL_END_DISPATCH} and ${LABEL_BEGIN_DISPATCH}.`);
                return false;
            }

            const endTimeErr = validateDispatchTime(LABEL_END_DISPATCH, formData.endTimeAt);
            if (endTimeErr) {
                setError(endTimeErr);
                return false;
            }

            const beginTimeErr = validateDispatchTime(LABEL_BEGIN_DISPATCH, formData.beginTimeAt);
            if (beginTimeErr) {
                setError(beginTimeErr);
                return false;
            }

            const { leavingYardAt, backToYardAt } = buildDatetimesFromForm();
            if (!leavingYardAt || !backToYardAt) {
                setError('Could not build schedule from the dates and times provided.');
                return false;
            }

            const leaveMs = leavingYardAt.getTime();
            const backMs = backToYardAt.getTime();
            if (!Number.isFinite(leaveMs) || !Number.isFinite(backMs)) {
                setError(`Invalid ${LABEL_END_DISPATCH} or ${LABEL_BEGIN_DISPATCH}.`);
                return false;
            }
            if (backMs <= leaveMs) {
                setError(`${LABEL_BEGIN_DISPATCH} must be after ${LABEL_END_DISPATCH}.`);
                return false;
            }

            const { startDate, endDate } = derivePtoDates(leavingYardAt, backToYardAt);
            if (endDate < startDate) {
                setError(`${LABEL_BEGIN_DISPATCH} date cannot be before ${LABEL_END_DISPATCH} date.`);
                return false;
            }
        }

        if (!formData.requestType) {
            setError('Request type is required');
            return false;
        }

        if (formData.requestType === 'vacation_paid') {
            const spanDays = isOffice
                ? calendarSpanDays(formData.absenceStartDate, formData.absenceEndDate)
                : (() => {
                    const { leavingYardAt, backToYardAt } = buildDatetimesFromForm();
                    if (!leavingYardAt || !backToYardAt) return 0;
                    const { startDate, endDate } = derivePtoDates(leavingYardAt, backToYardAt);
                    return calendarSpanDays(startDate, endDate);
                })();
            const paidDays = Number(formData.paidVacationDays);
            if (!Number.isFinite(paidDays) || !Number.isInteger(paidDays) || paidDays < 1) {
                setError('Please select the number of paid vacation days to use.');
                return false;
            }
            if (paidDays > spanDays) {
                setError(`Paid vacation days cannot exceed ${spanDays} day(s) for this request.`);
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        setError('');

        try {
            const payload = {
                requestType: formData.requestType,
                reason: formData.reason,
                submitterTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
            if (formData.requestType === 'vacation_paid') {
                payload.paidVacationDays = Number(formData.paidVacationDays);
            }

            if (isOffice) {
                payload.absenceStartDate = formData.absenceStartDate;
                payload.absenceEndDate = formData.absenceEndDate;
                payload.isPartialDay = formData.dayMode === 'partial'
                    && isSameLocalDay(formData.absenceStartDate, formData.absenceEndDate);
                if (payload.isPartialDay) {
                    payload.partialDayStartTime = toHhMm(formData.leaveTimeAt);
                    payload.partialDayEndTime = toHhMm(formData.backTimeAt);
                }
            } else {
                const { leavingYardAt, backToYardAt } = buildDatetimesFromForm();
                const { startDate, endDate } = derivePtoDates(leavingYardAt, backToYardAt);
                payload.startDate = startDate;
                payload.endDate = endDate;
                payload.isPartialDay = false;
                payload.leavingYardAt = leavingYardAt;
                payload.backToYardAt = backToYardAt;
            }

            const response = await apiClient.post('/forms/time-off', payload);

            setSuccess('Time off request submitted successfully!');
            setFormData(createInitialFormData());

            setTimeout(() => {
                setFormOpen(false);
                setSuccess('');
                if (onSubmitSuccess) onSubmitSuccess(response?.data?.data);
            }, 2000);

        } catch (err) {
            console.error('Error submitting time off request:', err);
            setError(err.response?.data?.message || 'Failed to submit request');
        } finally {
            setLoading(false);
        }
    };

    const calculateDurationDays = () => {
        if (isOffice) {
            return calendarSpanDays(formData.absenceStartDate, formData.absenceEndDate);
        }
        const { leavingYardAt, backToYardAt } = buildDatetimesFromForm();
        if (!leavingYardAt || !backToYardAt) return 0;
        const { startDate, endDate } = derivePtoDates(leavingYardAt, backToYardAt);
        return calendarSpanDays(startDate, endDate);
    };

    const officeSingleDay = isSameLocalDay(formData.absenceStartDate, formData.absenceEndDate);

    const patchOfficeStart = (v) => {
        const start = v ? startOfLocalDay(v) : null;
        const end = formData.absenceEndDate ? startOfLocalDay(formData.absenceEndDate) : start;
        const nextEnd = start && end && end < start ? start : formData.absenceEndDate;
        const next = { absenceStartDate: v, absenceEndDate: nextEnd };
        if (nextEnd && start && !isSameLocalDay(start, nextEnd) && formData.dayMode === 'partial') {
            next.dayMode = 'all';
        }
        patchForm(next);
    };

    const patchOfficeEnd = (v) => {
        const next = { absenceEndDate: v };
        if (v && formData.absenceStartDate && !isSameLocalDay(formData.absenceStartDate, v) && formData.dayMode === 'partial') {
            next.dayMode = 'all';
        }
        patchForm(next);
    };

    const pickerMinDate = startOfLocalDay(new Date());
    const durationDays = calculateDurationDays();

    const paidVacationDayOptions = useMemo(() => {
        const max = Math.max(1, durationDays || 1);
        return Array.from({ length: max }, (_, i) => i + 1);
    }, [durationDays]);

    const handleRequestTypeChange = (requestType) => {
        const patch = { requestType };
        if (requestType === 'vacation_paid') {
            const max = Math.max(1, durationDays || 1);
            const current = Number(formData.paidVacationDays);
            patch.paidVacationDays =
                Number.isInteger(current) && current >= 1 && current <= max
                    ? current
                    : max;
        } else {
            patch.paidVacationDays = null;
        }
        patchForm(patch);
    };

    useEffect(() => {
        if (formData.requestType !== 'vacation_paid') return;
        const max = Math.max(1, durationDays || 1);
        setFormData((prev) => {
            const current = Number(prev.paidVacationDays);
            if (Number.isInteger(current) && current >= 1 && current <= max) {
                return prev;
            }
            return { ...prev, paidVacationDays: max };
        });
    }, [durationDays, formData.requestType]);

    return (
        <Paper sx={{ p: compact ? 2 : 3, mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={compact ? 1 : 2}>
                <Typography
                    variant={compact ? 'h5' : 'h6'}
                    sx={compact ? { fontWeight: 800, color: '#E94F37', lineHeight: 1.15, borderLeft: '4px solid #E94F37', pl: 1.25 } : undefined}
                >
                    Submit Time Off Request
                </Typography>
                {!compact && (
                    <IconButton onClick={() => setFormOpen(!formOpen)}>
                        {formOpen ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                )}
            </Stack>

            <Collapse in={formOpen}>
                <Box component="form" onSubmit={handleSubmit}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    <Grid container spacing={compact ? 2 : 3}>
                        {isOffice ? (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                                        Dates of Absence
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        label="From"
                                        value={formData.absenceStartDate}
                                        onChange={patchOfficeStart}
                                        minDate={pickerMinDate}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                required: true,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        label="To"
                                        value={formData.absenceEndDate}
                                        onChange={patchOfficeEnd}
                                        minDate={formData.absenceStartDate || pickerMinDate}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                required: true,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset" required>
                                        <FormLabel>Day type</FormLabel>
                                        <RadioGroup
                                            row
                                            value={officeSingleDay ? formData.dayMode : 'all'}
                                            onChange={(e) => patchForm({ dayMode: e.target.value })}
                                        >
                                            <FormControlLabel value="all" control={<Radio />} label="All Day" />
                                            <FormControlLabel
                                                value="partial"
                                                control={<Radio />}
                                                label="Partial Day"
                                                disabled={!officeSingleDay}
                                            />
                                        </RadioGroup>
                                        {!officeSingleDay && (
                                            <Typography variant="caption" color="text.secondary">
                                                Partial day is available when From and To are the same date.
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                                {officeSingleDay && formData.dayMode === 'partial' && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TimePicker
                                                label="Leave at"
                                                value={formData.leaveTimeAt}
                                                onChange={(v) => patchForm({ leaveTimeAt: v })}
                                                ampm
                                                minutesStep={15}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        required: true,
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TimePicker
                                                label="Back at"
                                                value={formData.backTimeAt}
                                                onChange={(v) => patchForm({ backTimeAt: v })}
                                                ampm
                                                minutesStep={15}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        required: true,
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                                        Terminal Schedule
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DispatchScheduleBlock
                                        dateLabel={LABEL_END_DISPATCH}
                                        dateValue={formData.endDispatchDate}
                                        onDateChange={(v) => patchForm({ endDispatchDate: v })}
                                        timeAt={formData.endTimeAt}
                                        onTimeAtChange={(v) => patchForm({ endTimeAt: v })}
                                        minDate={pickerMinDate}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DispatchScheduleBlock
                                        dateLabel={LABEL_BEGIN_DISPATCH}
                                        dateValue={formData.beginDispatchDate}
                                        onDateChange={(v) => patchForm({ beginDispatchDate: v })}
                                        timeAt={formData.beginTimeAt}
                                        onTimeAtChange={(v) => patchForm({ beginTimeAt: v })}
                                        minDate={pickerMinDate}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mb: 1 }}>
                                Doctor appointments require a doctor’s excuse; future requests may be denied if documentation is not provided.
                            </Alert>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Requests for absences other than sick leave must be submitted at least two weeks before the first day of the absence.
                            </Alert>
                        </Grid>

                        <Grid item xs={12} md={formData.requestType === 'vacation_paid' ? 6 : 12}>
                            <FormControl fullWidth required>
                                <InputLabel>Request Type</InputLabel>
                                <Select
                                    value={formData.requestType}
                                    label="Request Type"
                                    onChange={(e) => handleRequestTypeChange(e.target.value)}
                                >
                                    {requestTypes.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {formData.requestType === 'vacation_paid' && (
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Paid vacation days to use</InputLabel>
                                    <Select
                                        value={
                                            formData.paidVacationDays != null
                                                ? String(formData.paidVacationDays)
                                                : ''
                                        }
                                        label="Paid vacation days to use"
                                        onChange={(e) =>
                                            patchForm({ paidVacationDays: Number(e.target.value) })
                                        }
                                        disabled={durationDays < 1}
                                    >
                                        {paidVacationDayOptions.map((n) => (
                                            <MenuItem key={n} value={String(n)}>
                                                {n} day{n !== 1 ? 's' : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                                Duration: {durationDays} day{durationDays !== 1 ? 's' : ''}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Reason (Optional)"
                                multiline
                                rows={3}
                                value={formData.reason}
                                onChange={(e) => patchForm({ reason: e.target.value })}
                                placeholder="Please provide any additional details about your request..."
                                inputProps={{ maxLength: 500 }}
                                helperText={`${formData.reason.length}/500 characters`}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setFormOpen(false);
                                        setError('');
                                        setSuccess('');
                                    }}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <Add />}
                                >
                                    {loading ? 'Submitting...' : 'Submit Request'}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default TimeOffRequestForm;
